import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Select } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';

const METHOD_OPTIONS = [
  { key: '*', text: '*（全部方法）', value: '*' },
  { key: 'GET', text: 'GET', value: 'GET' },
  { key: 'POST', text: 'POST', value: 'POST' },
  { key: 'PUT', text: 'PUT', value: 'PUT' },
  { key: 'DELETE', text: 'DELETE', value: 'DELETE' },
  { key: 'PATCH', text: 'PATCH', value: 'PATCH' },
];

const STATUS_OPTIONS = [
  { key: 1, text: '启用', value: 1 },
  { key: 2, text: '禁用', value: 2 },
];

const EditApiProduct = () => {
  const params = useParams();
  const navigate = useNavigate();
  const productId = params.id;
  const isEdit = productId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const originInputs = {
    name: '',
    path: '',
    method: '*',
    upstream_url: '',
    price: 0,
    cache_ttl: 0,
    timeout: 30,
    status: 1,
    description: '',
    upstream_headers: '',
  };
  const [inputs, setInputs] = useState(originInputs);
  // 库内 price 以「额度」存储（QuotaPerUnit 额度 = ¥1），页面统一按人民币元录入与展示
  const [quotaPerUnit, setQuotaPerUnit] = useState(0);

  const handleCancel = () => {
    navigate('/api-product');
  };

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const resolveQuotaPerUnit = async () => {
    const cached = parseFloat(localStorage.getItem('quota_per_unit'));
    if (cached > 0) {
      return cached;
    }
    const res = await API.get('/api/status');
    const { data } = res.data || {};
    const unit = parseFloat(data && data.quota_per_unit);
    if (unit > 0) {
      localStorage.setItem('quota_per_unit', String(data.quota_per_unit));
      return unit;
    }
    return 0;
  };

  const loadProduct = async (unit) => {
    const res = await API.get(`/api/api_product/${productId}`);
    const { success, message, data } = res.data;
    if (success) {
      setInputs({
        ...originInputs,
        ...data,
        price: data.price > 0 ? Number((data.price / unit).toFixed(6)) : 0,
        upstream_headers: data.upstream_headers || '',
      });
    } else {
      showError(message);
    }
  };

  useEffect(() => {
    (async () => {
      const unit = await resolveQuotaPerUnit();
      if (unit > 0) {
        setQuotaPerUnit(unit);
        if (isEdit) {
          await loadProduct(unit);
        }
      } else {
        showError('无法获取额度换算比例，请刷新页面后重试');
      }
      setLoading(false);
    })();
  }, []);

  const submit = async () => {
    if (!inputs.name || !inputs.path || !inputs.upstream_url) {
      showError('名称、路径和上游地址不能为空');
      return;
    }
    // 元 → 额度（库内口径）；parseFloat 保留小数，单价 ¥0.05 这类小额才存得进去
    const priceYuan = parseFloat(inputs.price);
    if (Number.isNaN(priceYuan) || priceYuan < 0) {
      showError('价格必须是不小于 0 的数字（单位：元）');
      return;
    }
    if (quotaPerUnit <= 0) {
      showError('无法获取额度换算比例，请刷新页面后重试');
      return;
    }
    const payload = {
      ...inputs,
      price: Math.round(priceYuan * quotaPerUnit),
      cache_ttl: parseInt(inputs.cache_ttl) || 0,
      timeout: parseInt(inputs.timeout) || 30,
      status: parseInt(inputs.status) || 1,
    };
    let res;
    if (isEdit) {
      res = await API.put('/api/api_product/', {
        ...payload,
        id: parseInt(productId),
      });
    } else {
      res = await API.post('/api/api_product/', payload);
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(isEdit ? '更新成功' : '创建成功');
      if (!isEdit) {
        setInputs(originInputs);
      }
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? '更新 API' : '新建 API'}
          </Card.Header>
          <Form loading={loading}>
            <Form.Field>
              <Form.Input
                label='名称'
                name='name'
                placeholder='例如：IP 查询'
                onChange={handleInputChange}
                value={inputs.name}
                autoComplete='off'
              />
            </Form.Field>
            <Form.Group widths='equal'>
              <Form.Input
                label='对外路径（相对 /openapi）'
                name='path'
                placeholder='/ip/query'
                onChange={handleInputChange}
                value={inputs.path}
                autoComplete='off'
              />
              <Form.Field>
                <label>请求方法</label>
                <Select
                  name='method'
                  options={METHOD_OPTIONS}
                  value={inputs.method}
                  onChange={handleInputChange}
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <Form.Input
                label='上游地址'
                name='upstream_url'
                placeholder='https://example.com/ip/query'
                onChange={handleInputChange}
                value={inputs.upstream_url}
                autoComplete='off'
              />
            </Form.Field>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>价格（元 / 次）</label>
                <Form.Input
                  name='price'
                  type='number'
                  min='0'
                  step='0.0001'
                  placeholder='0 表示免费，不扣费'
                  onChange={handleInputChange}
                  value={inputs.price}
                />
                <span style={{ color: '#888', fontSize: 12 }}>
                  单位：人民币元，支持小数（如 0.05）。实际扣费 = 单价 × 调用账号的
                  分组倍率；短信/邮件/公众号按接收人逐条计费。
                  {quotaPerUnit > 0 && parseFloat(inputs.price) > 0 && (
                    <>
                      {' '}
                      本次将保存为 {Math.round(parseFloat(inputs.price) * quotaPerUnit)}{' '}
                      额度。
                    </>
                  )}
                </span>
              </Form.Field>
              <Form.Input
                label='缓存秒数（0 表示不缓存）'
                name='cache_ttl'
                type='number'
                onChange={handleInputChange}
                value={inputs.cache_ttl}
              />
              <Form.Input
                label='超时秒数'
                name='timeout'
                type='number'
                onChange={handleInputChange}
                value={inputs.timeout}
              />
            </Form.Group>
            <Form.Field>
              <label>状态</label>
              <Select
                name='status'
                options={STATUS_OPTIONS}
                value={inputs.status}
                onChange={handleInputChange}
              />
            </Form.Field>
            <Form.Field>
              <Form.TextArea
                label='描述'
                name='description'
                placeholder='可选'
                onChange={handleInputChange}
                value={inputs.description}
              />
            </Form.Field>
            <Form.Field>
              <Form.TextArea
                label='上游额外 Header（JSON）'
                name='upstream_headers'
                placeholder='{"Authorization":"Bearer xxx"}'
                onChange={handleInputChange}
                value={inputs.upstream_headers}
              />
            </Form.Field>
            <Button onClick={handleCancel}>取消</Button>
            <Button type='submit' onClick={submit} positive>
              提交
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditApiProduct;
